import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FcPlus } from "react-icons/fc";
import { FaRegEdit } from "react-icons/fa";
import {
  MdOutlineCancel,
  MdOutlineContentCopy,
  MdCheck,
  MdLink,
} from "react-icons/md";
import BoltTable from "../../../../../../../Components/BoltTable/BoltTable";
import BoltButton from "../../../../../../../Components/UI/Buttons/BoltButton";
import Tooltip from "../../../../../../../Components/Tooltip/Tooltip";
import Confirm from "../../../../../../../Components/Popups/Confirm";
import ReferralFormModal from "./ReferralFormModal";
import {
  fetchReferrals,
  fetchReferralStatistics,
  deleteReferral,
} from "../../../../../../../services/referral.services";
import {
  formatMarkup,
  isUsableReferral,
} from "../../../../../../../helpers/referral";

const Badge = ({ children }) => (
  <span className="rounded-full bg-state-neutral-bg px-[6px] py-px text-[10px] font-semibold uppercase tracking-wide text-state-neutral-text">
    {children}
  </span>
);

const StatCard = ({ label, value }) => (
  <div className="border border-ui-border rounded-lg p-4 bg-ui-surface">
    <div className="text-xs font-medium text-ui-text-muted">{label}</div>
    <div className="text-2xl font-semibold text-ui-text mt-1">
      {value ?? 0}
    </div>
  </div>
);

const ReferralsManager = ({ setSnack }) => {
  const { t } = useTranslation("settings");
  const location = useLocation();
  const navigate = useNavigate();
  const [referralsData, setReferralsData] = useState({ data: [] });
  const [statistics, setStatistics] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [refresh, setRefresh] = useState(false);
  const [copied, setCopied] = useState(null);

  const notify = (text, severity) =>
    setSnack?.((s) => ({ ...s, open: true, text, severity }));

  const loadStatistics = async () => {
    const res = await fetchReferralStatistics();
    if (!res?.error) setStatistics(res?.data?.result ?? null);
  };

  const loadReferrals = async (page, perPage, filter) => {
    const res = await fetchReferrals(page, perPage, filter);
    if (res?.error) {
      notify(
        res?.error?.response?.data?.message ?? t("referrals.loadFailed"),
        "error"
      );
      return;
    }
    setReferralsData(res?.data?.result ?? { data: [] });
  };

  useEffect(() => {
    loadStatistics();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  // "Create custom referral" in the navbar's Refer & Earn modal lands here
  // with the form open. The state is cleared so Back or a refresh doesn't
  // open it again.
  useEffect(() => {
    if (location.state?.openCreate) {
      openCreate();
      navigate(location.pathname, { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  const openEdit = (row) => {
    setEditing(row);
    setFormOpen(true);
  };

  const handleSaved = () => {
    setRefresh((r) => !r);
    loadStatistics();
  };

  const handleDelete = async (row) => {
    setConfirmDelete(null);
    try {
      const res = await deleteReferral(row?.id);
      if (res?.error) {
        notify(
          res?.error?.response?.data?.message ?? t("referrals.deleteFailed"),
          "error"
        );
        return;
      }
      notify(res?.data?.message ?? t("referrals.deleted"), "success");
      setRefresh((r) => !r);
      loadStatistics();
    } catch (error) {
      notify(
        error?.response?.data?.message ?? t("referrals.deleteFailed"),
        "error"
      );
    }
  };

  const buildReferralLink = (code) =>
    `${window.location.origin}/register/shipper?ref=${encodeURIComponent(code)}`;

  const copyToClipboard = async (text, id, type, successText) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied({ id, type });
      setTimeout(() => setCopied(null), 2000);
      if (successText) notify(successText, "success");
    } catch {
      notify(t("referrals.copyFailed"), "error");
    }
  };

  const columns = [
    {
      id: 0,
      label: "#",
      field_name: "id",
      render: (row) => <>{row?.id ?? "-"}</>,
      width: "80px",
    },
    {
      id: 1,
      label: t("referrals.code"),
      field_name: "code",
      render: (row) => {
        const code = row?.code ?? row?.referral_code;
        if (!code) return <>-</>;
        const isCopied = copied?.type === "code" && copied?.id === row?.id;
        return (
          <div className="flex items-center gap-2">
            <span className="font-medium">{code}</span>
            <Tooltip title={isCopied ? t("referrals.copiedBang") : t("referrals.copyCode")}>
              <button
                type="button"
                aria-label={t("referrals.copyCode")}
                className="text-ui-text-muted hover:text-brand transition-colors"
                onClick={() => copyToClipboard(code, row?.id, "code")}
              >
                {isCopied ? (
                  <MdCheck size={15} className="text-state-success" />
                ) : (
                  <MdOutlineContentCopy size={15} />
                )}
              </button>
            </Tooltip>
            {row?.referral_type === "default" && <Badge>{t("referrals.default")}</Badge>}
            {!isUsableReferral(row) && <Badge>{t("referrals.inactive")}</Badge>}
          </div>
        );
      },
      width: "180px",
    },
    {
      id: 2,
      label: t("referrals.rewardRate"),
      field_name: "markup",
      render: (row) => <>{formatMarkup(row?.markup, row?.markup_type)}</>,
      width: "140px",
    },
    {
      id: 3,
      label: t("referrals.maxUsers"),
      field_name: "max_users",
      render: (row) => <>{row?.max_users ?? "-"}</>,
      width: "120px",
    },
    {
      id: 4,
      label: t("referrals.usersReferred"),
      field_name: "total_users",
      render: (row) => <>{row?.total_users ?? 0}</>,
      width: "140px",
    },
    {
      id: 5,
      label: t("fields.actions"),
      field_name: "actions",
      render: (row) => {
        const code = row?.code ?? row?.referral_code;
        const isLinkCopied = copied?.type === "link" && copied?.id === row?.id;
        // The backend refuses to edit or delete the default referral, and to
        // delete one that someone has already signed up with.
        const isDefault = row?.referral_type === "default";
        const isUsed = Number(row?.total_users ?? 0) > 0;
        // Referrals are % only for now; editing a flat-amount one would
        // quietly turn it into a percentage, so it stays read-only.
        const isFlat = row?.markup_type === "$";
        return (
          <div className="table_action__wrap">
            {code ? (
              <Tooltip
                title={
                  isLinkCopied
                    ? t("referrals.linkCopiedBang")
                    : t("referrals.copyLink")
                }
              >
                <div
                  className="table_action__btn"
                  onClick={() =>
                    copyToClipboard(
                      buildReferralLink(code),
                      row?.id,
                      "link",
                      t("referrals.linkCopied")
                    )
                  }
                >
                  {isLinkCopied ? <MdCheck size={10} /> : <MdLink size={10} />}
                  <span>{isLinkCopied ? t("referrals.copied") : t("referrals.getLink")}</span>
                </div>
              </Tooltip>
            ) : null}
            {!isDefault &&
              (isFlat ? (
                <Tooltip title={t("referrals.flatNoEdit")}>
                  <span>
                    <div className="table_action__btn table_action__btn__disabled">
                      <FaRegEdit size={10} />
                      <span>{t("common:actions.edit")}</span>
                    </div>
                  </span>
                </Tooltip>
              ) : (
                <Tooltip title={t("referrals.editReferral")}>
                  <div
                    className="table_action__btn"
                    onClick={() => openEdit(row)}
                  >
                    <FaRegEdit size={10} />
                    <span>{t("common:actions.edit")}</span>
                  </div>
                </Tooltip>
              ))}
            {!isDefault &&
              (isUsed ? (
                <Tooltip title={t("referrals.usedNoDelete")}>
                  {/* The span receives the hover; the disabled button ignores pointer events. */}
                  <span>
                    <div className="table_action__btn table_action__btn__disabled">
                      <MdOutlineCancel size={10} />
                      <span>{t("common:actions.delete")}</span>
                    </div>
                  </span>
                </Tooltip>
              ) : (
                <Tooltip title={t("referrals.deleteReferral")}>
                  <div
                    className="table_action__btn"
                    onClick={() => setConfirmDelete(row)}
                  >
                    <MdOutlineCancel size={10} />
                    <span>{t("common:actions.delete")}</span>
                  </div>
                </Tooltip>
              ))}
          </div>
        );
      },
      width: "280px",
      fixed: "right",
    },
  ];

  return (
    <>
      <Confirm
        open={!!confirmDelete}
        setOpen={setConfirmDelete}
        handleConfirm={() => handleDelete(confirmDelete)}
        text={t("referrals.deleteConfirm")}
      />

      <ReferralFormModal
        open={formOpen}
        setOpen={setFormOpen}
        previousData={editing}
        onSaved={handleSaved}
        setSnack={setSnack}
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <StatCard label={t("referrals.stats.total")} value={statistics?.total_referrals} />
        <StatCard
          label={t("referrals.stats.active")}
          value={statistics?.active_referrals}
        />
        <StatCard
          label={t("referrals.stats.usersReferred")}
          value={statistics?.total_users_referred}
        />
      </div>

      <BoltTable
        tableTitle={t("referrals.tableTitle")}
        tableId="referralsTable"
        message={t("referrals.empty")}
        searchPlaceHolder={t("referrals.search")}
        columns={columns}
        data={referralsData}
        getAllData={loadReferrals}
        refresh={refresh}
        disableAutoResizing
        maxHeight="calc(100vh - 320px)"
        headerButtons={
          <div className="flex gap-1 flex-wrap">
            <BoltButton
              icon={FcPlus}
              label={t("referrals.createReferral")}
              onClick={openCreate}
              variant="success"
              size="sm"
            />
          </div>
        }
      />
    </>
  );
};

export default ReferralsManager;
